import React from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { inputSliceActions, selectActiveEventTypeId, selectActiveTeamOption } from 'features/input/inputSlice';
import { keyboardActions, selectActiveFocusItem } from 'features/keyboard/keyboardSlice';

import { ActiveFocusItemName } from 'features/keyboard/keyboardSliceTypes';

import IconButton from 'atoms/IconButton';
import eventIcons from 'constants/icons';

import { selectTime } from '../../features/time/timeSlice';
import { matchSliceActions, selectMatch } from '../../features/match/matchSlice';
import { EventButtonProps } from './EventButtonProps';
import { MatchEventType } from '../../enums/matchEventType';
import { TeamOption } from '../../enums/teamOption';
import { MatchStatus } from '../../enums/matchStatus';
import getPeriodTimeFromMatchLength from '../MatchWatch/helpers/getPeriodTimeFromMatchLength';
import hasExtraTime from '../../helpers/hasExtraTime';

const EventButton: React.FC<EventButtonProps> = ({ matchEventTypeId, team, disabled }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const activeMatchEventTypeId = useSelector(selectActiveEventTypeId);
  const activeTeam = useSelector(selectActiveTeamOption);
  const time = useSelector(selectTime);
  const activeFocusItem = useSelector(selectActiveFocusItem, shallowEqual);
  const match = useSelector(selectMatch);

  const _hasExtraTime = hasExtraTime(match.matchEvents);
  const isVar = matchEventTypeId === MatchEventType.VAR;
  const isInjuryTime = matchEventTypeId === MatchEventType.INJURY_TIME;
  const isVarActive = isVar && activeMatchEventTypeId === MatchEventType.VAR;
  const isInjuryTimeActive = isInjuryTime && activeMatchEventTypeId === MatchEventType.INJURY_TIME;
  const isButtonActive = activeMatchEventTypeId === matchEventTypeId && activeTeam === team;
  const eventIcon: string = eventIcons[matchEventTypeId];
  const alt = intl.formatMessage({ id: `events.${matchEventTypeId}` });
  const focused =
    matchEventTypeId === MatchEventType.GOAL &&
    activeFocusItem === (team === TeamOption.HOME ? ActiveFocusItemName.HOME_GOAL : ActiveFocusItemName.AWAY_GOAL);

  const onClick = () => {
    if (!disabled) {
      dispatch(matchSliceActions.clearActiveMatchEvents());
      dispatch(inputSliceActions.clearActiveSelect());
      dispatch(inputSliceActions.newEvent());
      let eventTime = time.minutes + ':' + time.seconds.toString().padStart(2, '0');
      if (match.matchStatusId === MatchStatus.PENALTIES) {
        let time;
        if (_hasExtraTime) {
          time = getPeriodTimeFromMatchLength(MatchStatus.PENALTIES, match.matchLength);
        } else {
          time = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, match.matchLength);
        }
        eventTime = time + ':00';
      }
      dispatch(inputSliceActions.setEventTime(eventTime));
      dispatch(
        inputSliceActions.setActive({
          team: team ?? null,
          eventTypeId: matchEventTypeId,
        }),
      );
    }
  };

  const onBlur = () => {
    dispatch(keyboardActions.setActiveFocusItem(null));
  };

  if (isVar) {
    return (
      <IconButton
        icon={eventIcon}
        alt={alt}
        className={classNames({
          active: isVarActive,
        })}
        onClick={onClick}
        noText
      />
    );
  } else if (isInjuryTime) {
    return (
      <IconButton
        icon={eventIcon}
        alt={alt}
        className={classNames({
          active: isInjuryTimeActive,
          disabled: disabled,
        })}
        onClick={onClick}
        noText
      />
    );
  }

  return (
    <IconButton
      icon={eventIcon}
      alt={alt}
      className={classNames({
        active: isButtonActive,
      })}
      onClick={onClick}
      onBlur={onBlur}
      focused={focused}
      title={alt}
    />
  );
};

export default EventButton;
